import { Dispatch, Fragment, SetStateAction, useState } from 'react';
import { ArrowLeftIcon, KeyIcon, UserPlusIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { useMutation } from '@apollo/client/react';

import {
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  DialogContent,
} from '@/components/ui/dialog';
import { SetupSteps } from '@/components/Integration/Setup';
import { renderLogo } from 'src/utils/renderLogo';
import { customIntegrations } from 'src/shared/entities';
import { Button } from '@/components/ui/button';
import { ICustomIntegrationInputAttributes, CustomIntegrationProviderEnum } from 'src/graphql/schema';
import { CreateCustomIntegrationDocument } from 'src/graphql/actions/integration/createCustomIntegration.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { cn } from 'src/lib/utils';

import { useIntegration, customIntegrationSetupMap } from '../model';
import { refetchIntegrations } from '../api';

import { customIntegrationInstructionMap } from './customIntegrationInstructionMap';

type IStep0Data = {
  key: 0;
  items: React.ReactNode[];
};

type IStep1Data = {
  key: 1;
  form: React.ComponentType<{ onSubmit: (values: unknown) => Promise<void>; onBack: () => void }>;
};

type IStep2Data = {
  key: 2;
};

export type IStepData = (IStep0Data | IStep1Data | IStep2Data) & {
  title: string;
  description: string;
  modalWidth: string;
};

export const StepRenderer = ({
  stepData,
  setStep,
  onCreated,
  integrationId,
}: {
  stepData: IStepData;
  setStep: Dispatch<SetStateAction<number>>;
  onCreated: (id: string) => void;
  integrationId: string | null;
}) => {
  const { provider } = useIntegration();
  const [createCustomIntegration] = useMutation(CreateCustomIntegrationDocument, {
    onCompleted: refetchIntegrations,
  });
  const navigate = useNavigate();

  switch (stepData.key) {
    case 0: {
      const handleClick = () => setStep(1);

      return (
        <Fragment>
          <SetupSteps items={stepData.items} />
          <Button onClick={handleClick} className="w-full" size="sm">
            <KeyIcon />
            Enter access credentials
          </Button>
        </Fragment>
      );
    }
    case 1: {
      const handleSubmit = async (values: unknown) => {
        try {
          const { data } = await createCustomIntegration({
            variables: {
              customIntegrationInput: {
                ...(values as ICustomIntegrationInputAttributes),
                provider: provider as CustomIntegrationProviderEnum,
              },
            },
          });
          if (data?.createCustomIntegration?.errors?.[0]) throw new ClientError(data.createCustomIntegration.errors[0]);
          const id = data?.createCustomIntegration?.customIntegration?.id ?? '';
          onCreated(id);
        } catch (error) {
          getApiError(error, toast.error);
        }
      };
      const handleBack = () => setStep(0);
      const Form = stepData.form;
      return <Form onSubmit={handleSubmit} onBack={handleBack} />;
    }
    case 2: {
      const onBack = () => navigate('/integrations');
      const onShareAccess = () => navigate(`/integrations/${integrationId}`, { state: { openShareAccess: true } });

      return (
        <DialogFooter className="md:justify-center md:gap-6">
          <DialogClose asChild>
            <Button variant="secondary" size="sm" className="sm:flex-1" onClick={onBack}>
              <ArrowLeftIcon /> Back to integrations
            </Button>
          </DialogClose>
          <DialogClose asChild>
            <Button size="sm" className="sm:flex-1" onClick={onShareAccess}>
              <UserPlusIcon /> Share access
            </Button>
          </DialogClose>
        </DialogFooter>
      );
    }
    default:
      return null;
  }
};

const CustomIntegrationSetup = () => {
  const [step, setStep] = useState(0);
  const [integrationId, setIntegrationId] = useState<string | null>(null);
  const { provider } = useIntegration();

  if (!provider || !customIntegrations.includes(provider as (typeof customIntegrations)[number]))
    return <h1 className="text-center text-2xl font-bold">This integration is not supported yet.</h1>;

  const getStepData = (): IStepData => {
    switch (step) {
      case 0: {
        const data = customIntegrationInstructionMap[provider as keyof typeof customIntegrationInstructionMap];
        return {
          key: 0,
          modalWidth: 'md:max-w-2xl',
          ...data,
        };
      }
      case 1: {
        const data = customIntegrationSetupMap[provider as keyof typeof customIntegrationSetupMap];
        return {
          key: 1,
          modalWidth: 'md:max-w-xl',
          ...data,
        };
      }
      case 2: {
        const title =
          customIntegrationSetupMap[provider as keyof typeof customIntegrationSetupMap].title + ' is now connected';
        return {
          key: 2,
          title,
          description: `You can now select ${provider === 'aws' ? 'buckets' : 'repositories'} from ${customIntegrationSetupMap[provider as keyof typeof customIntegrationSetupMap].title} and deposit them into your escrow or Software Backup.\n\nShare access with team members so they can use this integration in escrows they are members of.`,
          modalWidth: 'md:max-w-lg',
        };
      }
      default:
        return {
          key: 0,
          title: '',
          description: '',
          modalWidth: 'md:max-w-0',
          items: [],
        };
    }
  };

  const onIntegrationCreated = (id: string) => {
    setIntegrationId(id);
    setStep(2);
  };

  const stepData = getStepData();
  const Icon = renderLogo(provider);

  return (
    <DialogContent className={cn('gap-6 max-h-205 overflow-y-auto', stepData.modalWidth)}>
      <DialogHeader className={cn('relative text-center gap-0', step === 1 && 'pl-16 text-left')}>
        {Icon && <Icon className={cn('size-10 mx-auto mb-1.5', step === 1 && 'absolute top-0 left-0')} />}
        <DialogTitle className={cn('text-xl font-bold mb-4 leading-5', step === 1 && 'mb-1.5')}>
          {stepData.title}
        </DialogTitle>
        <DialogDescription className="font-medium whitespace-pre-wrap">{stepData.description}</DialogDescription>
      </DialogHeader>
      <StepRenderer
        stepData={stepData}
        setStep={setStep}
        onCreated={onIntegrationCreated}
        integrationId={integrationId}
      />
    </DialogContent>
  );
};

export default CustomIntegrationSetup;
